// "ממודעה קיימת לסט באנרים באותו סגנון": מעלים PDF או תמונה, המערכת קוראת כותרת וצבעים,
// ובונה מחדש באנרים בכל הגדלים. הוויזואל נשמר, הכותרת נכתבת מחדש בעיצוב הבאנר.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { ErrorBox, Field, Loading, useMe } from '../components/ui';
import Icon from '../components/Icon';
import { SitePick, type MediaPick } from './QuickCampaign';

interface Ad {
  assetId: string; url: string; width: number; height: number; isPdf: boolean; pages: number; fonts: string[];
  headline: string; subline: string; cta: string; colors: { primary: string; cta: string; text: string; background: string };
  fontStyle: string; fontWeight: string; hasEmbeddedText: boolean; mood: string; aiUsed: boolean;
}
const FONT_STYLE: Record<string, string> = { sans: 'גרוטסק (ללא סריפים)', serif: 'סריפי', display: 'כותרת דקורטיבית', script: 'כתב יד' };
const COLOR_LABELS: [keyof Ad['colors'], string][] = [['primary', 'צבע ראשי'], ['cta', 'צבע כפתור'], ['text', 'צבע טקסט'], ['background', 'צבע רקע']];

export default function AdToBanners() {
  const me = useMe();
  const nav = useNavigate();
  const [clients, setClients] = useState<{ id: string; name: string }[] | null>(null);
  const [media, setMedia] = useState<any[] | null>(null);
  const [clientId, setClientId] = useState('');
  const [newClient, setNewClient] = useState('');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [ad, setAd] = useState<Ad | null>(null);
  const [clean, setClean] = useState(true);
  const [picks, setPicks] = useState<MediaPick[]>([]);
  const [newSite, setNewSite] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/clients').then(setClients).catch((e) => setError(e.message));
    api.get('/api/quick/media').then(setMedia).catch(() => setMedia([]));
  }, []);

  const ensureClient = async (): Promise<string | null> => {
    if (clientId) return clientId;
    if (!newClient.trim()) { setError('בחרו לקוח או הקלידו שם ללקוח חדש'); return null; }
    const c = await api.post('/api/clients', { name: newClient.trim() });
    setClientId(c.id); setClients((x) => [...(x ?? []), c]);
    return c.id as string;
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null); setNotice(null);
    try {
      setBusy('קורא את המודעה…');
      const cid = await ensureClient();
      if (!cid) return;
      const fd = new FormData(); fd.append('clientId', cid); fd.append('file', file);
      const r: Ad = await api.upload('/api/quick/ad-analyze', fd);
      setAd(r); setClean(r.hasEmbeddedText);
      if (!name.trim() && r.headline) setName(r.headline.slice(0, 60));
      if (!r.aiUsed) setNotice('ה-AI לא מחובר או לא הצליח לקרוא את המודעה, לכן הכותרת והצבעים הם הערכה בלבד. בדקו ותקנו ידנית.');
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };

  const toggleSite = (m: any) => setPicks((p) => p.some((x) => x.siteId === m.id) ? p.filter((x) => x.siteId !== m.id) : [...p, { siteId: m.id, name: m.name, sizes: [...m.sizes], known: m.sizes, zipMaxKb: m.zipMaxKb ?? 150, confirmSpec: me.canApproveSpecs }]);
  const addSite = () => { const n = newSite.trim(); if (!n) return; setPicks((p) => [...p, { newName: n, name: n, sizes: [], known: [], zipMaxKb: 150, confirmSpec: me.canApproveSpecs }]); setNewSite(''); };

  const create = async () => {
    setError(null);
    if (!ad) { setError('העלו מודעה'); return; }
    if (!ad.headline.trim()) { setError('הכותרת ריקה. הקלידו אותה'); return; }
    if (!name.trim()) { setError('תנו שם לקמפיין'); return; }
    try { new URL(url); } catch { setError('כתובת הנחיתה לא תקינה — צריכה להתחיל ב-https://'); return; }
    if (!picks.length || picks.some((p) => !p.sizes.length)) { setError('בחרו אתר וגדלים (לכל אתר לפחות גודל אחד)'); return; }
    try {
      let imageId = ad.assetId;
      if (clean && ad.hasEmbeddedText) {
        setBusy('מנקה את הטקסט מהתמונה (עד חצי דקה)…');
        try { const c = await api.post('/api/quick/ad-clean', { assetId: ad.assetId }); imageId = c.assetId; }
        catch (e: any) { setNotice(`הניקוי לא הצליח (${e.message}). ממשיכים עם התמונה המקורית, והטקסט שבה עלול להופיע בכפל.`); }
      }
      setBusy('מקים קמפיין…');
      const s = await api.post('/api/quick/setup', {
        clientId, brand: { mode: 'plain', name: `${name.trim()} (סגנון מודעה)`, colors: ad.colors }, imageAssetId: imageId, masterAspect: ad.width && ad.height ? ad.width / ad.height : undefined,
        media: picks.map((p) => ({ siteId: p.siteId, newName: p.newName, sizes: p.sizes, zipMaxKb: p.zipMaxKb, confirmSpec: p.confirmSpec })),
        campaign: { name: name.trim(), headline: ad.headline.trim(), subline: ad.subline.trim(), cta: ad.cta.trim() || 'לפרטים', disclaimer: '', url: url.trim() },
        prompt: `שמרו על הסגנון הוויזואלי של המודעה המקורית: צבעי המותג ותחושה (${ad.mood || 'כמו במודעה'}), עם כותרת בעובי כבד. התמונה המצורפת היא הוויזואל הראשי. בטקסט משתמשים בכותרת בלבד${ad.subline ? ' ובמשפט המשנה' : ''}.`,
      });
      if (ad.aiUsed) {
        setBusy('משחזר את המבנה, הצבעים והפונטים של המודעה (עד כדקה)…');
        try {
          const m = await api.post('/api/quick/ad-master', { variantId: s.masterVariantId, assetId: ad.assetId, cleanAssetId: imageId !== ad.assetId ? imageId : null, texts: { headline: ad.headline.trim(), subline: ad.subline.trim(), cta: ad.cta.trim() }, colors: ad.colors });
          if (!m.ok) setNotice(`שחזור המבנה לא הצליח (${m.reason}). הבאנר ייווצר בעיצוב חדש לפי הסגנון.`);
        } catch (e: any) { setNotice(`שחזור המבנה לא הצליח (${e.message}). הבאנר ייווצר בעיצוב חדש לפי הסגנון.`); }
      }
      nav(`/new?set=${s.setId}&master=${s.masterVariantId}&campaign=${s.campaignId}`);
    } catch (e: any) { setError(e.message); setBusy(null); }
  };

  if (!clients || !media) return <Loading />;
  return (
    <div className="stack" style={{ maxWidth: 900, gap: 20 }}>
      <div className="page-title"><div><h1>ממודעה קיימת לסט באנרים</h1><p>מעלים מודעה (PDF או תמונה). המערכת משחזרת את המבנה (שורות בצבעיהן, כפתור, תמונה, לוגו) ובונה מזה באנרים בכל הגדלים. אחרי זה אפשר לתקן במילים.</p></div></div>
      {busy && <div className="alert info" role="status"><span className="spinner" /> {busy}</div>}
      <ErrorBox error={error} />
      {notice && <div className="alert warn" role="status">{notice}</div>}

      <section className="card stack">
        <h2>1. לקוח וקמפיין</h2>
        <div className="grid cols-2">
          <Field label="לקוח"><select value={clientId} onChange={(e) => { setClientId(e.target.value); setNewClient(''); }}><option value="">— לקוח חדש (הקלידו שם) —</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          {!clientId && <Field label="שם לקוח חדש"><input value={newClient} onChange={(e) => setNewClient(e.target.value)} /></Field>}
          <Field label="שם הקמפיין *"><input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="כתובת נחיתה *" hint="לשם הלחיצה על הבאנר תוביל"><input className="ltr" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" /></Field>
        </div>
      </section>

      <section className="card stack">
        <h2>2. המודעה</h2>
        <Field label="קובץ PDF או תמונה" hint="ב-PDF נשתמש בעמוד הראשון"><input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void onFile(f); }} /></Field>
        {ad && (
          <div className="grid cols-2" style={{ alignItems: 'start' }}>
            <div className="stack">
              <img src={ad.url} alt="המודעה שהועלתה" style={{ maxWidth: '100%', maxHeight: 340, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />
              {ad.hasEmbeddedText && <label className="row small"><input type="checkbox" checked={clean} onChange={(e) => setClean(e.target.checked)} /> ניקוי הטקסט מהתמונה לפני השימוש (AI, כ-0.07$). בלי זה הטקסט המקורי יופיע בכפל עם הכותרת החדשה.</label>}
            </div>
            <div className="stack">
              <Field label="כותרת *" hint="נקראה אוטומטית. בדקו כל מילה, כי ייתכנו טעויות קריאה (למשל ד/ז)"><input value={ad.headline} onChange={(e) => setAd({ ...ad, headline: e.target.value })} /></Field>
              <Field label="משפט משנה (לא חובה)" hint="גם אותו כדאי לבדוק מילה במילה"><input value={ad.subline} onChange={(e) => setAd({ ...ad, subline: e.target.value })} /></Field>
              <Field label="טקסט כפתור"><input value={ad.cta} onChange={(e) => setAd({ ...ad, cta: e.target.value })} placeholder="לפרטים" /></Field>
              <div className="grid cols-2">
                {COLOR_LABELS.map(([k, l]) => <Field key={k} label={l}><span className="row"><input type="color" value={ad.colors[k]} onChange={(e) => setAd({ ...ad, colors: { ...ad.colors, [k]: e.target.value.toUpperCase() } })} /><input className="ltr mono" style={{ width: 96 }} value={ad.colors[k]} onChange={(e) => setAd({ ...ad, colors: { ...ad.colors, [k]: e.target.value } })} /></span></Field>)}
              </div>
              <div className="alert info small">
                <strong>פונט:</strong> {ad.fonts.length ? `ב-PDF נמצאו הפונטים: ${ad.fonts.join(', ')}.` : `זיהוי פונט מתמונה הוא השערה בלבד (הוצע: ${FONT_STYLE[ad.fontStyle] ?? ad.fontStyle}).`} המערכת בוחרת מתוך מאגר של פונטים עבריים את הפונט הדומה ביותר. כדי לשמור על הפונט המקורי בדיוק, מעלים את קובץ הפונט ב"לקוחות ← מותגים".
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="card stack">
        <h2>3. אתרים וגדלים</h2>
        <div className="q-sites">
          {media.map((m) => (
            <label key={m.id} className={`q-brand ${picks.some((p) => p.siteId === m.id) ? 'sel' : ''}`}>
              <input type="checkbox" checked={picks.some((p) => p.siteId === m.id)} onChange={() => toggleSite(m)} />
              <span><strong>{m.name}</strong><span className="small muted ltr">{m.sizes.map((s: any) => `${s.width}x${s.height}`).join(' · ') || 'אין מידות שמורות'}</span></span>
            </label>
          ))}
        </div>
        <div className="row"><input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="אתר חדש" aria-label="שם אתר חדש" onKeyDown={(e) => { if (e.key === 'Enter') addSite(); }} /><button className="btn" onClick={addSite}>הוספה</button></div>
        {picks.map((p, i) => <SitePick key={(p.siteId ?? '') + p.name} p={p} canApprove={me.canApproveSpecs} onChange={(x) => setPicks((all) => all.map((y, j) => (j === i ? { ...y, ...x } : y)))} onRemove={() => setPicks((all) => all.filter((_, j) => j !== i))} />)}
      </section>

      <div><button className="btn primary big" onClick={create} disabled={!!busy || !ad}><Icon name="wand" size={22} />בנו לי באנרים</button></div>
    </div>
  );
}
